import axios from "axios";
import { API } from "./vikas";

export const UNAVAILABLE = "VIKAS chat is unavailable.";

// a refusal carrying the door's own words (or the fallback) and the HTTP status
export class DoorError extends Error {
  constructor(message, status = 0) { super(message || UNAVAILABLE); this.status = status; }
}

async function bodyError(res) {
  try {
    const j = await res.json();
    if (j && typeof j.error === "string" && j.error.trim()) return new DoorError(j.error, res.status);
  } catch { /* empty or broken body */ }
  return new DoorError(UNAVAILABLE, res.status);
}

// a fresh 5-minute pass before each door call
export async function getPass() {
  const r = await axios.get(`${API}/chat-pass`);
  return r.data;
}

// call the door; on 401 get a new pass once and retry, then give up
async function doorFetch(path, init) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const p = await getPass();
    if (!p.ready) return { notReady: true };
    const res = await fetch(`${p.door}${path}`, {
      ...init,
      headers: { ...(init.headers || {}), Authorization: `Bearer ${p.pass}` },
    });
    if (res.status !== 401 || attempt === 1) return { res };
  }
  return { res: null };
}

export async function doorReady() {
  try { return (await getPass()).ready === true; } catch { return false; }
}

// POST /chat, one JSON event per "data:" line
export async function streamChat(body, onEvent) {
  const { res, notReady } = await doorFetch("/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
  });
  if (notReady) return { out: "not-ready" };
  if (!res || !res.body) throw new DoorError(UNAVAILABLE, res?.status || 0);
  let said = false;
  const seen = (e) => { if ((e.type === "limit" || e.type === "error") && typeof e.message === "string" && e.message) said = true; onEvent(e); };
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop();
    lines.forEach((ln) => {
      if (!ln.startsWith("data:")) return;
      let e = null;
      try { e = JSON.parse(ln.slice(5).trim()); } catch { /* skip a broken line */ }
      if (e) seen(e);
    });
  }
  if (!res.ok && !said) throw new DoorError(UNAVAILABLE, res.status);
  return { out: "ok", status: res.status };
}

export async function loadConversation(id) {
  const { res, notReady } = await doorFetch(`/conversation?id=${encodeURIComponent(id)}`, { method: "GET" });
  if (notReady) return null;
  if (!res) throw new DoorError(UNAVAILABLE);
  if (!res.ok) throw await bodyError(res);
  return (await res.json()).messages || [];
}

export async function getStarters() {
  const { res, notReady } = await doorFetch("/starters", { method: "GET" });
  if (notReady || !res || !res.ok) return [];
  try { const j = await res.json(); return Array.isArray(j.starters) ? j.starters.filter((x) => typeof x === "string" && x) : []; } catch { return []; }
}

export async function sendFeedback(body) {
  const { res, notReady } = await doorFetch("/feedback", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (notReady || !res) throw new DoorError(UNAVAILABLE);
  if (!res.ok) throw await bodyError(res);
}

export async function doorSearch(q) {
  const { res, notReady } = await doorFetch("/search", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ q }),
  });
  if (notReady) return null;
  if (!res) throw new DoorError(UNAVAILABLE);
  if (!res.ok) throw await bodyError(res);
  return (await res.json()).hits || [];
}

export const getConvId = async () => (await axios.get(`${API}/chat-conversation`)).data.conversation_id || "";
export const setConvId = (id) => axios.put(`${API}/chat-conversation`, { conversation_id: id || "" });
